import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, ChevronRight, FolderOpen, RotateCcw, Save, X } from 'lucide-react'
import { api, ApiRequestError, type ProductionSettings, type ProductionFolderInspection } from '../api'
import { Button, IconButton, Select } from './ui'
import { IconTooltip } from './IconTooltip'
import { WorkspaceAction, WorkspaceActions } from './WorkspaceHeader'
import './ProductionFoldersSettings.css'

type FolderKind = 'media' | 'assets'
type FolderDraft = Pick<ProductionSettings, 'media_dir' | 'assets_dir' | 'expected_isos'> & { lower_third_overrides: Record<string, string> }
declare global {
  interface Window {
    motionFolders?: { chooseFolder: (options: { kind: FolderKind; currentPath?: string }) => Promise<string | null> }
  }
}

function editable(settings: ProductionSettings): FolderDraft {
  return { media_dir: settings.media_dir, assets_dir: settings.assets_dir, expected_isos: settings.expected_isos, lower_third_overrides: settings.lower_third_overrides || {} }
}
function sameDraft(a: FolderDraft, b: FolderDraft) {
  return a.media_dir === b.media_dir && a.assets_dir === b.assets_dir && a.expected_isos === b.expected_isos && JSON.stringify(a.lower_third_overrides) === JSON.stringify(b.lower_third_overrides)
}
function graphicsRoot(path: string | null) {
  return path?.replace(/\/+$/, '').replace(/\/(lower-thirds|bugs)$/i, '') || null
}
function folderName(path: string) {
  const parts = path.split('/').filter(Boolean)
  return parts.at(-1)?.toLowerCase() === 'video' ? parts.slice(-3).join(' / ') : parts.at(-1) || path
}

function FolderIssue({ message, details, children }: { message: string; details?: string; children?: React.ReactNode }) {
  return <div className="production-folders-error" role="alert"><AlertCircle size={15} aria-hidden="true" /><div><p>{message}</p>{children}{details ? <details><summary>Details</summary><pre>{details}</pre></details> : null}</div></div>
}

export function ProductionFoldersSettings({ hidden = false }: { hidden?: boolean }) {
  const [base, setBase] = useState<ProductionSettings | null>(null)
  const [draft, setDraft] = useState<FolderDraft | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<'media' | 'assets' | 'save' | 'inspect' | null>(null)
  const [inspection, setInspection] = useState<ProductionFolderInspection | null>(null)
  const [speaker, setSpeaker] = useState('')
  const [graphic, setGraphic] = useState('')
  const [readError, setReadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const [saved, setSaved] = useState(false)
  const scope = useRef(0)
  const draftRef = useRef(draft)
  const baseRef = useRef(base)
  const previousHidden = useRef(hidden)
  draftRef.current = draft; baseRef.current = base
  const native = typeof window.motionFolders?.chooseFolder === 'function'
  const dirty = Boolean(base && draft && !sameDraft(draft, editable(base)))
  const graphicsRootChanged = Boolean(base && draft && graphicsRoot(base.assets_dir) !== graphicsRoot(draft.assets_dir))
  useEffect(() => () => { scope.current += 1 }, [])
  async function load(discardDraft = false) {
    const currentScope = scope.current
    setLoading(true)
    try {
      const result = await api.productionSettings()
      if (currentScope !== scope.current) return
      const hasChanges = Boolean(baseRef.current && draftRef.current && !sameDraft(draftRef.current, editable(baseRef.current)))
      if (discardDraft || !hasChanges) {
        setBase(result); setDraft(editable(result)); setConflict(false); setError(null); setSaved(false); setInspection(null); setGraphic('')
      } else if (result.revision !== baseRef.current?.revision) setConflict(true)
      else setBase(result)
      setReadError(null)
    } catch (err) {
      if (currentScope === scope.current) setReadError(err instanceof Error ? err.message : 'Could not load production folders.')
    } finally { if (currentScope === scope.current) setLoading(false) }
  }
  useEffect(() => { if (native && !hidden && !base && !loading && !readError) void load() }, [native, hidden, base, loading, readError])
  useEffect(() => {
    const returning = previousHidden.current && !hidden
    previousHidden.current = hidden
    if (returning && native && base && !busy && !loading) void load()
  }, [hidden, native, base, busy, loading])
  function change(patch: Partial<FolderDraft>) {
    if ('media_dir' in patch || 'assets_dir' in patch || 'expected_isos' in patch) { setInspection(null); setGraphic('') }
    setDraft(value => value ? { ...value, ...patch, ...('assets_dir' in patch && graphicsRoot(patch.assets_dir ?? null) !== graphicsRoot(value.assets_dir) ? { lower_third_overrides: {} } : {}) } : value); setSaved(false); setError(null)
  }
  async function inspect() {
    if (!draft || !base || busy || loading || readError || conflict) return
    const currentScope = scope.current
    setBusy('inspect'); setError(null); setInspection(null); setGraphic('')
    try {
      const result = await api.inspectProductionFolders({ media_dir: draft.media_dir, assets_dir: draft.assets_dir, expected_isos: draft.expected_isos, revision: base.revision })
      if (currentScope === scope.current) setInspection(result)
    } catch (err) {
      if (currentScope !== scope.current) return
      if (err instanceof ApiRequestError && err.status === 409) setConflict(true)
      setError(err instanceof Error ? err.message : 'Could not check folders. Reconnect the drive and try again.')
    } finally { if (currentScope === scope.current) setBusy(null) }
  }
  async function choose(kind: FolderKind) {
    if (!native || !draft || busy || loading || readError) return
    const currentScope = scope.current
    setBusy(kind); setError(null)
    try {
      const currentPath = kind === 'media' ? draft.media_dir : draft.assets_dir
      const selected = await window.motionFolders!.chooseFolder({ kind, ...(currentPath ? { currentPath } : {}) })
      if (currentScope !== scope.current || selected === null) return
      change(kind === 'media' ? { media_dir: selected } : { assets_dir: selected })
    } catch (err) {
      if (currentScope === scope.current) setError(err instanceof Error ? err.message : 'The folder picker could not open. Try again.')
    } finally { if (currentScope === scope.current) setBusy(null) }
  }
  async function save(event: FormEvent) {
    event.preventDefault()
    if (!native || !base || !draft || !dirty || busy || loading || readError || conflict) return
    const currentScope = scope.current
    setBusy('save'); setError(null); setSaved(false)
    try {
      const result = await api.saveProductionSettings({ ...draft, revision: base.revision })
      if (currentScope !== scope.current) return
      setBase(result); setDraft(editable(result)); setSaved(true)
    } catch (err) {
      if (currentScope !== scope.current) return
      if (err instanceof ApiRequestError && err.status === 409) setConflict(true)
      setError(err instanceof Error ? err.message : 'Production folders could not be saved. Your choices are still here.')
    } finally { if (currentScope === scope.current) setBusy(null) }
  }
  const disabled = !native || !draft || loading || Boolean(readError) || Boolean(busy)
  return <section className="production-folders-section" aria-label="Production folders">
    {!hidden && dirty ? <WorkspaceActions>
      <WorkspaceAction label="Reset production folder changes" disabled={loading || Boolean(busy)} onClick={() => void load(true)}><RotateCcw size={18} /></WorkspaceAction>
      <WorkspaceAction label={busy === 'save' ? 'Saving production folders…' : 'Save production folders'} type="submit" form="production-folders-form" disabled={disabled || conflict}><Save size={18} /></WorkspaceAction>
    </WorkspaceActions> : null}
    <h3>Folders</h3>
    {!native ? <p className="production-folders-message" role="status">Choose folders in Motion on the editor Mac. Each Mac keeps its own locations.</p>
      : readError ? <FolderIssue message={`Could not load production folders.${draft ? ' Your unsaved choices are still here.' : ''}`} details={readError}>
        <Button variant="secondary" size="sm" disabled={loading || Boolean(busy)} onClick={() => void load()}>{loading ? 'Loading…' : 'Retry'}</Button>
      </FolderIssue> : !base ? <p className="production-folders-message" role="status">Loading folders…</p> : null}
    {native && draft && base ? <form id="production-folders-form" onSubmit={event => void save(event)} aria-label="Production folder settings">
      <fieldset disabled={disabled}>
        {(['media', 'assets'] as const).map(kind => {
          const path = kind === 'media' ? draft.media_dir : draft.assets_dir
          const savedPath = kind === 'media' ? base.media_dir : base.assets_dir
          const available = kind === 'media' ? base.media_available : base.assets_available
          const label = kind === 'media' ? 'Session media' : 'Lower thirds & graphics'
          return <div className="production-folder-item" key={kind}>
            <div className="production-folder-row">
              <button type="button" className="production-folder-choose" aria-label={`Choose ${label.toLowerCase()} folder`} title={path || (kind === 'media' ? 'Choose the folder containing session camera recordings' : 'Choose your lower-thirds folder or the folder containing lower-thirds and Graphics')} onClick={() => void choose(kind)}>
                <FolderOpen size={17} aria-hidden="true" /><span className="production-folder-label">{label}</span><span className={`production-folder-value${path ? '' : ' is-empty'}`}>{busy === kind ? 'Opening…' : path ? folderName(path) : 'Choose folder'}</span><ChevronRight size={15} aria-hidden="true" />
              </button>
              {path ? <IconTooltip label={`Remove ${label.toLowerCase()} folder`} side="bottom"><IconButton type="button" label={`Remove ${label.toLowerCase()} folder`} className="icon-button production-folder-remove" onClick={() => change(kind === 'media' ? { media_dir: null } : { assets_dir: null })}><X size={14} aria-hidden="true" /></IconButton></IconTooltip> : null}
            </div>
            {path && path === savedPath && !available ? <p className="production-folder-unavailable"><AlertCircle size={13} aria-hidden="true" />Folder unavailable. Connect the drive or choose another folder.</p> : null}
          </div>
        })}
        <label className="production-camera-row"><span>Camera count</span><Select aria-label="Camera count" value={draft.expected_isos} onChange={event => change({ expected_isos: Number(event.target.value) })}>{[1, 2, 3, 4, 5].map(count => <option key={count} value={count}>{count} {count === 1 ? 'camera' : 'cameras'}</option>)}</Select></label>
        <div className="production-folder-inspection">
          <Button type="button" variant="secondary" size="sm" onClick={() => void inspect()} disabled={disabled || conflict}>{busy === 'inspect' ? 'Checking…' : 'Check folders'}</Button>
          {inspection ? <div role="status">
            {draft.media_dir ? <p>{inspection.camera_roles.length} camera roles · {inspection.recording_count} recordings found. Synchronization is checked during handoff.</p> : null}
            {inspection.issues.map((issue, index) => <p key={index}>{issue}</p>)}
            {inspection.media_candidates.length ? <label>Session or talk folder<Select aria-label="Session or talk folder" value="" onChange={event => change({ media_dir: event.target.value })}><option value="">Choose a Video folder</option>{inspection.media_candidates.map(item => <option key={item.path} value={item.path}>{item.label}</option>)}</Select></label> : null}
          </div> : null}
          <details className="production-graphics-choices"><summary>Lower-third choices</summary>
            <p>Use a speaker’s exact name and choose the role and duration you want. Check folders to list the available files.</p>
            {Object.entries(draft.lower_third_overrides).map(([name, file]) => <div className="production-graphic-choice" key={name}><span>{name} · {file}</span><Button type="button" variant="secondary" size="sm" onClick={() => { const choices = { ...draft.lower_third_overrides }; delete choices[name]; change({ lower_third_overrides: choices }) }}>Remove</Button></div>)}
            {graphicsRootChanged ? <p>Save the graphics folder, then check folders to choose its lower thirds.</p> : null}
            {!graphicsRootChanged && inspection?.graphics_files.length ? <div className="production-graphic-choice-fields">
              <label>Speaker name<input aria-label="Lower-third speaker name" value={speaker} onChange={event => setSpeaker(event.target.value)} /></label>
              <label>File<Select aria-label="Lower-third file" value={graphic} onChange={event => setGraphic(event.target.value)}><option value="">Choose a file</option>{inspection.graphics_files.map(file => <option key={file} value={file}>{file}</option>)}</Select></label>
              <Button type="button" variant="secondary" size="sm" disabled={!speaker.trim() || !inspection.graphics_files.includes(graphic)} onClick={() => { change({ lower_third_overrides: { ...draft.lower_third_overrides, [speaker.trim()]: graphic } }); setSpeaker(''); setGraphic('') }}>Use for speaker</Button>
            </div> : null}
          </details>
        </div>
      </fieldset>
      {conflict ? <FolderIssue message="Production settings changed elsewhere. Your choices are still here. Reload saved settings before making new changes."><Button type="button" variant="secondary" size="sm" disabled={loading || Boolean(busy)} onClick={() => void load(true)}>Reload saved settings</Button></FolderIssue>
        : error ? <FolderIssue message="Production folders could not be changed. Your choices are still here." details={error} /> : null}
      <span className="sr-only" role="status">{dirty ? 'Unsaved changes' : saved ? 'Saved on this Mac' : ''}</span>
    </form> : null}
  </section>
}
